/**
 * Tela de troca obrigatória da senha provisória (E65.F3, ADR-0069). // ux-neutrality:allow doc-comment
 *
 * Sem saída lateral de propósito: quem chega aqui não alcança nenhuma outra // ux-neutrality:allow doc-comment
 * superfície do produto até trocar, e oferecer um link para "voltar" só // ux-neutrality:allow doc-comment
 * produziria uma volta para o 403. O gate REAL é do servidor — esta tela existe // ux-neutrality:allow doc-comment
 * para explicar a recusa e resolvê-la, não para produzi-la. // ux-neutrality:allow doc-comment
 *
 * O estado vem do servidor (`senha_provisoria` no perfil), nunca do formulário // ux-neutrality:allow doc-comment
 * de login: a pessoa pode ter fechado o navegador entre o login e a troca. // ux-neutrality:allow doc-comment
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { Blueprint, Button, Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole, resetCurrentRoleCache } from '@/lib/auth/useCurrentRole';
import { homeRouteForAmbiente, PERITO_HOME_ROUTE } from '@/lib/auth/homeRoute';
import { trocarSenha, TrocaDeSenhaError } from '@/lib/perfil';

const MIN_PASSWORD_LENGTH = 8;

/** Recusas que a tela resolve sozinha, sem gastar uma ida ao servidor. */
function validarLocalmente(
  atual: string,
  nova: string,
  confirmacao: string,
  t: typeof ptBR.trocaDeSenha,
): string | null {
  if (nova.length < MIN_PASSWORD_LENGTH) return t.erroSenhaCurta;
  if (nova !== confirmacao) return t.erroConfirmacao;
  // Trocar a provisória por ela mesma deixaria a conta exatamente no estado que
  // esta tela existe para encerrar — e o servidor aceitaria, porque o hash muda
  // de qualquer forma.
  if (nova === atual) return t.erroSenhaRepetida;
  return null;
}

/** Recusa do servidor → frase de ação. Código cru nunca chega à tela. */
function mensagemDoErro(err: unknown, t: typeof ptBR.trocaDeSenha): string {
  if (!(err instanceof TrocaDeSenhaError)) return t.erroDesconhecido;
  if (err.reason === 'senha_atual_incorreta') return t.erroSenhaAtual;
  if (err.reason === 'validation') return t.erroSenhaCurta;
  if (err.reason === 'network') return t.erroRede;
  return t.erroDesconhecido;
}

export function TrocarSenhaPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const t = ptBR.trocaDeSenha;
  const { ambiente, loading, error: perfilError, senhaProvisoria } = useCurrentRole();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const casa = ambiente !== null ? homeRouteForAmbiente(ambiente) : PERITO_HOME_ROUTE;
  const tipoDoCampo = mostrar ? 'text' : 'password';

  // Quem não tem pendência não tem o que fazer aqui. A checagem é do SERVIDOR
  // (o perfil já respondeu); só depois de resolvido é que decidimos devolver.
  if (!loading && !perfilError && !senhaProvisoria) {
    return <Navigate to={casa} replace />;
  }

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setError(null);
    const problema = validarLocalmente(senhaAtual, senhaNova, confirmacao, t);
    if (problema !== null) {
      setError(problema);
      return;
    }
    setSubmitting(true);
    try {
      await trocarSenha(apiClient, { senha_atual: senhaAtual, senha_nova: senhaNova });
      // A marca sumiu no servidor, mas o perfil guardado em memória ainda diz
      // que existe. Sem o reset, o guard devolveria a pessoa para cá em laço.
      resetCurrentRoleCache();
      navigate(casa, { replace: true });
    } catch (err) {
      setError(mensagemDoErro(err, t));
      setSubmitting(false);
    }
  }

  return (
    <Blueprint
      as="section"
      data-testid="trocar-senha-page"
      aria-labelledby="trocar-senha-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
        {t.eyebrow}
      </p>
      <h1 id="trocar-senha-title" className="font-display text-[32px] leading-none text-fg">
        {t.pageTitle}
      </h1>
      <p className="mt-1.5 text-body-sm text-fg-muted">{t.intro}</p>

      <form onSubmit={handleSubmit} className="mt-[26px] flex flex-col gap-4">
        <Field label={t.senhaAtualLabel} htmlFor="troca-senha-atual" description={t.senhaAtualHint}>
          <Input
            id="troca-senha-atual"
            type={tipoDoCampo}
            required
            autoComplete="current-password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            disabled={submitting}
            data-testid="troca-senha-atual"
          />
        </Field>

        <Field label={t.senhaNovaLabel} htmlFor="troca-senha-nova" description={t.senhaNovaHint}>
          <div className="relative flex">
            <Input
              id="troca-senha-nova"
              type={tipoDoCampo}
              required
              autoComplete="new-password"
              value={senhaNova}
              onChange={(e) => setSenhaNova(e.target.value)}
              disabled={submitting}
              className="pr-10"
              data-testid="troca-senha-nova"
            />
            <button
              type="button"
              onClick={() => setMostrar((s) => !s)}
              aria-label={
                mostrar ? ptBR.resetPassword.passwordHide : ptBR.resetPassword.passwordShow
              }
              aria-pressed={mostrar}
              data-testid="troca-senha-toggle"
              className="absolute inset-y-0 right-2 grid w-7 place-items-center text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              {mostrar ? (
                <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
              ) : (
                <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
              )}
            </button>
          </div>
        </Field>

        <Field label={t.confirmacaoLabel} htmlFor="troca-senha-confirmacao">
          <Input
            id="troca-senha-confirmacao"
            type={tipoDoCampo}
            required
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            disabled={submitting}
            data-testid="troca-senha-confirmacao"
          />
        </Field>

        {error ? (
          <div
            role="alert"
            data-testid="troca-senha-error"
            className="border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            {error}
          </div>
        ) : null}

        <Button type="submit" disabled={submitting} data-testid="troca-senha-submit">
          {submitting ? t.submitting : t.submit}
        </Button>
      </form>
    </Blueprint>
  );
}
